import { useCallback, useEffect, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { SUGGESTED_FOLLOWERS } from "MVVM/GraphQL/suggestedFollowers";
import { ISuggestedUser, ESuggestedFollowReason } from "../@types";
import { useUser } from "MVVM/Hooks/useUser";
import { SuggestedFollowing } from "MVVM/State/suggestedFollowing";
import { isSuggestedUsersCacheStale } from "MVVM/Utilities/Cache";
import { SuggestedUsersState } from "MVVM/State/suggestedUsers";

const LEGACY_REASON_MAPPING: Record<string, string> = {
  PLAYED_TOGETHER: "Played Together",
  FOLLOWED_BY_FOLLOWING: "Followed by Following",
  RISING_STAR: "Rising Star",
  UNSPECIFIED: "Unspecified",
  POPULAR_USER: "Popular User",
};

const mapReasonToDisplay = (reason: string): string => {
  if (LEGACY_REASON_MAPPING[reason]) {
    return LEGACY_REASON_MAPPING[reason];
  }

  if (ESuggestedFollowReason[reason as keyof typeof ESuggestedFollowReason]) {
    return ESuggestedFollowReason[
      reason as keyof typeof ESuggestedFollowReason
    ];
  }

  return ESuggestedFollowReason.UNSPECIFIED;
};

const SuggestedFeedViewModel = () => {
  const { allstarUser } = useUser();
  const loggedIn = allstarUser?.loggedIn;
  const suggestedFollowing: { ids: string[] } =
    useRecoilValue(SuggestedFollowing);

  let useGraphQuery = loggedIn;

  const cacheKey = `${loggedIn ? "logged-in" : "logged-out"}_${
    useGraphQuery ? "graph" : "legacy"
  }_v2`;

  const suggestedUsersCacheState = useRecoilValue(SuggestedUsersState);
  const setSuggestedUsersCache = useSetRecoilState(SuggestedUsersState);

  const currentCacheState = suggestedUsersCacheState[cacheKey] || {
    suggestedFollowers: [],
    lastUpdated: 0,
  };

  const hasCachedData = currentCacheState.suggestedFollowers.length > 0;
  const isCacheStale = isSuggestedUsersCacheStale(
    currentCacheState.lastUpdated,
  );
  const shouldFetchFromNetwork = !hasCachedData || isCacheStale;

  const { data, error, loading } = useQuery(
    SUGGESTED_FOLLOWERS(useGraphQuery),
    {
      fetchPolicy: shouldFetchFromNetwork ? "network-only" : "cache-first",
      variables: { count: 12 },
      skip: !shouldFetchFromNetwork,
    },
  );

  const filteredSuggestedFollowers = useCallback(() => {
    const suggestedFollowers = data?.me?.suggestions;
    if (!suggestedFollowers) return [];

    const filtered = suggestedFollowers.filter(
      ({ user: { _id } }: { user: { _id: string } }) =>
        !suggestedFollowing.ids.includes(_id),
    );

    const usersWithValidNames = filtered.filter(({ user }: any) => {
      const hasUsername = user.username && user.username !== "User";
      const hasSteamUsername = user.steam?.personaname;
      return hasUsername || hasSteamUsername;
    });

    const mappedFiltered = usersWithValidNames.map((suggestion: any) => ({
      ...suggestion,
      reason: mapReasonToDisplay(suggestion.reason),
    }));

    const sortedMapped = mappedFiltered.sort((a: any, b: any) => {
      const aIsFollowBack = a.reason === "Follow Back";
      const bIsFollowBack = b.reason === "Follow Back";

      if (aIsFollowBack && !bIsFollowBack) return -1;
      if (!aIsFollowBack && bIsFollowBack) return 1;
      return 0;
    });

    return sortedMapped.length > 10
      ? sortedMapped
      : suggestedFollowers.slice(0, 12).map((suggestion: any) => ({
          ...suggestion,
          reason: mapReasonToDisplay(suggestion.reason),
        }));
  }, [data?.me?.suggestions, suggestedFollowing.ids]);

  const suggestedFollowers = useMemo(() => {
    let followers = useGraphQuery
      ? filteredSuggestedFollowers()
      : data?.suggestedCreators?.map((suggested: ISuggestedUser) => {
          const mappedReason = mapReasonToDisplay(suggested.reason);
          return {
            user: suggested.user,
            reason: mappedReason,
          };
        }) || [];

    followers = followers.sort(
      (a: { reason: string }, b: { reason: string }) => {
        const aIsFollowBack = a.reason === "Follow Back";
        const bIsFollowBack = b.reason === "Follow Back";

        if (aIsFollowBack && !bIsFollowBack) return -1;
        if (!aIsFollowBack && bIsFollowBack) return 1;
        return 0;
      },
    );

    return followers;
  }, [useGraphQuery, filteredSuggestedFollowers, data?.suggestedCreators]);

  useEffect(() => {
    if (suggestedFollowers && suggestedFollowers.length > 0) {
      const processUsersWithAvatars = async () => {
        const formattedFollowers = await Promise.all(
          suggestedFollowers.map(async (user: any) => {
            const u = user.user ? user.user : user;
            let avatar = u.avatar;

            if (!avatar) {
              try {
                const response = await fetch(
                  `${process.env.REACT_APP_AWS_SERVERLESS}/get-random-avatar`,
                );
                const data = await response.json();
                avatar = data.url;
              } catch (_e) {
                console.log("Failed to fetch random avatar, using default.");
              }
            }

            return {
              _id: u._id,
              username: u.username || "User",
              avatar,
              reason: user.reason,
              steam: u.steam,
              userGroup: {
                type: u.userGroup?.type,
              },
              rank: u.rank,
            };
          }),
        );

        setSuggestedUsersCache((prevState) => ({
          ...prevState,
          [cacheKey]: {
            suggestedFollowers: formattedFollowers,
            lastUpdated: Date.now(),
          },
        }));
      };

      processUsersWithAvatars();
    }
  }, [suggestedFollowers, setSuggestedUsersCache, cacheKey]);

  const finalSuggestedFollowers =
    !shouldFetchFromNetwork && !loading
      ? currentCacheState.suggestedFollowers.map((cachedUser) => {
          return {
            user: {
              _id: cachedUser._id,
              username: cachedUser.username,
              avatar: cachedUser.avatar,
              steam: cachedUser.steam,
              userGroup: {
                type: cachedUser.userGroup?.type,
              },
              rank: cachedUser.rank,
            },
            reason: cachedUser.reason || ESuggestedFollowReason.UNSPECIFIED,
          };
        })
      : suggestedFollowers;

  return {
    suggestedFollowers: finalSuggestedFollowers,
    error,
    loading: shouldFetchFromNetwork ? loading : false,
    isCacheStale,
    lastCacheUpdate: new Date(currentCacheState.lastUpdated),
  };
};

export { SuggestedFeedViewModel };
